import React from 'react';
import Icon from '../ui/Icon';
import Modal from '../ui/Modal';
import { PHONE_W, PHONE_H, PHONE_LARGE_SCALE } from './PhonePreview';
import phone from './PhonePreview.module.css';
import { substituteParams } from './templateParams.util';
import stylesSelf from './RcsPreview.module.css';

// Real approved copy, fetched live from MSG91 (see rcsTemplate.service.js) —
// content is that project's real rich-card/carousel payload. `{{n}}`
// substitution is a fallback, not the primary path: MSG91's own captured
// templates for this account carry static copy with no placeholders.
function LiteralBody({ functionName, content, params }) {
  if (functionName === 'carousel') {
    const total = (content.titles || []).length;
    return (
      <>
        {content.image_urls?.[0] && <img src={content.image_urls[0]} alt="" className={stylesSelf.realImage} />}
        <div className={stylesSelf.cardBody}>
          <div className={stylesSelf.titleText}>{substituteParams(content.titles?.[0], params)}</div>
          {content.descriptions?.[0] && <div className={stylesSelf.descText}>{substituteParams(content.descriptions[0], params)}</div>}
          {total > 1 && <div className={stylesSelf.carouselBadge}>1 of {total} cards</div>}
        </div>
      </>
    );
  }

  // rich_card (and anything else we don't specifically recognize but still
  // has title/description/media_url in the same shape).
  return (
    <>
      {content.media_url && <img src={content.media_url} alt="" className={stylesSelf.realImage} />}
      <div className={stylesSelf.cardBody}>
        <div className={stylesSelf.titleText}>{substituteParams(content.title, params)}</div>
        {content.description && <div className={stylesSelf.descText}>{substituteParams(content.description, params)}</div>}
      </div>
      {content.actions?.length > 0 && (
        <div className={stylesSelf.actions}>
          {content.actions.map((a, i) => <div key={i} className={stylesSelf.actionBtn}>{a.text_to_show}</div>)}
        </div>
      )}
    </>
  );
}

function StructuralBody({ templateId, params }) {
  return (
    <>
      <div className={stylesSelf.imagePlaceholder}>
        <span className="material-symbols-rounded" style={{ fontSize: 22, opacity: 0.35 }}>image</span>
      </div>
      <div className={stylesSelf.cardBody}>
        <div className={stylesSelf.tplName}>{templateId || 'rcs_template_id'}</div>
        {params.length > 0 ? (
          <div className={stylesSelf.params}>
            {params.map((p, i) => (
              <div key={i} className={stylesSelf.paramRow}><span className={stylesSelf.paramIdx}>{`{{${i + 1}}}`}</span> {p}</div>
            ))}
          </div>
        ) : (
          <div className={stylesSelf.paramsEmpty}>No params set</div>
        )}
      </div>
      <div className={stylesSelf.actions}>
        <div className={stylesSelf.actionBtn}>Learn more</div>
        <div className={stylesSelf.actionBtn}>View</div>
      </div>
    </>
  );
}

const STATUS_NOTES = {
  not_found: "Couldn't find this template — check the ID, or it's not approved yet.",
  error: "Couldn't fetch the live template — showing a generic mockup.",
};

function Frame({ templateId, templateParams, fetchedTemplate, fetchStatus, scale = 1 }) {
  const params = templateParams.split(',').map((p) => p.trim()).filter(Boolean);
  return (
    <div style={{ width: PHONE_W * scale, height: PHONE_H * scale }}>
      <div className={phone.phone} style={scale !== 1 ? { transform: `scale(${scale})`, transformOrigin: 'top left' } : undefined}>
        <div className={phone.notch} />
        <div className={phone.time}>9:41</div>
        <div className={stylesSelf.card}>
          {fetchedTemplate
            ? <LiteralBody functionName={fetchedTemplate.functionName} content={fetchedTemplate.content} params={params} />
            : <StructuralBody templateId={templateId} params={params} />}
          {!fetchedTemplate && fetchStatus && STATUS_NOTES[fetchStatus] && (
            <div className={stylesSelf.statusNote}>{STATUS_NOTES[fetchStatus]}</div>
          )}
        </div>
        {/* Covers whatever's already rendered (including a previous template's
            stale content) while a fetch triggered by a dropdown change is in
            flight — otherwise switching templates looked like nothing was
            happening until the new content silently replaced the old. */}
        {fetchStatus === 'loading' && (
          <div className={phone.loadingOverlay}>
            <div className={phone.spinner} />
            <div className={phone.loadingText}>Fetching template…</div>
          </div>
        )}
      </div>
    </div>
  );
}

class RcsPreview extends React.Component {
  constructor(props) {
    super(props);
    this.state = { expanded: false };
  }

  render() {
    const { templateId = '', templateParams = '', fetchedTemplate = null, fetchStatus = 'idle' } = this.props;
    const { expanded } = this.state;
    const captionSuffix = fetchedTemplate ? 'real approved copy' : 'not the final approved copy';

    return (
      <div className={phone.wrap}>
        <div className={phone.labelRow}>
          <span className={phone.label}>Live Preview</span>
          <button type="button" className={phone.expandBtn} title="Enlarge preview" onClick={() => this.setState({ expanded: true })}>
            <Icon name="open_in_full" size={11} />
          </button>
        </div>
        <Frame templateId={templateId} templateParams={templateParams} fetchedTemplate={fetchedTemplate} fetchStatus={fetchStatus} />
        <div className={phone.caption}>Rich card · {captionSuffix}</div>

        <Modal open={expanded} onClose={() => this.setState({ expanded: false })} title="RCS — full preview" width="fit-content">
          <Frame templateId={templateId} templateParams={templateParams} fetchedTemplate={fetchedTemplate} fetchStatus={fetchStatus} scale={PHONE_LARGE_SCALE} />
        </Modal>
      </div>
    );
  }
}

export default RcsPreview;
